<template>
  <div
    ref="tabMenu"
    class="tab-menu-container"
  >
    <div
      class="tab-menu"
    >
      <div class="tab-wrapper">
        <span
          v-for="(tab, index) in tabLabels"
          v-show="showExploreTab || tab !== 'Explore'"
          :key="tab"
          class="tab-item"
          :data-test="`tab-${tab.toLowerCase()}`"
          :class="{
            'active': tab === activeTab,
          }"
          @click="handleTabClicked(index)"
        >
          <span>{{ tab }}</span>
          <span
            v-if="showDiscussionPosts && tab === 'Discussions'"
            class="discussion-posts-count"
          >{{ discussionPostCount }}</span>
        </span>
        <pop-over
          v-if="showUseTabsPopover"
          use-circle
          icon-name="folder-outline"
          :position="popOverPosition"
        >
          <template #header>
            <span>Use tabs to dive deeper</span>
          </template>
          Review the question, discuss with peers, or dive deeper.
        </pop-over>
      </div>
    </div>
    <slot name="action-buttons"></slot>
  </div>
</template>

<script>
import PopOver from '@/components/singletons/PopOver';

export default {
  components: {
    PopOver,
  },
  props: {
    activeTab: {
      type: String,
    },
    discussionPostCount: {
      type: Number,
      default: 0,
    },
    showExploreTab: {
      type: Boolean,
      default: false,
    },
    tabLabels: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['tab-clicked'],
  data() {
    return {
      usedTabs: false,
      screenWidth: window.innerWidth,
    };
  },
  computed: {
    showDiscussionPosts() {
      return this.discussionPostCount > 0;
    },
    showUseTabsPopover() {
      if (this.usedTabs) return false;
      return this.$store.getters['flashcardStates/flashcardStateList'].length === 1;
    },
    popOverPosition() {
      return this.screenWidth < 1024 ? 'bottom' : 'right';
    },
  },
  mounted() {
    this.updateScreenWidth();
    window.addEventListener('resize', this.updateScreenWidth);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateScreenWidth);
  },
  methods: {
    handleTabClicked(index) {
      this.usedTabs = true;
      this.$emit('tab-clicked', index);
      if (index <= 1) this.scrollToBeginning();
      else if (index === this.tabLabels.length - 1) this.scrollToEnd();
    },
    scrollToBeginning() {
      if (process.env.NODE_ENV !== 'test') {
        this.$refs.tabMenu.scrollTo({ left: 0, behavior: 'smooth' });
      }
    },
    scrollToEnd() {
      if (process.env.NODE_ENV !== 'test') {
        const scrollWidth = this.$refs.tabMenu.scrollWidth;
        this.$refs.tabMenu.scrollTo({ left: scrollWidth, behavior: 'smooth' });
      }
    },
    updateScreenWidth() {
      this.screenWidth = window.innerWidth;
    },
  },
};
</script>

<style lang="scss" scoped>
.tab-menu-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  overflow-x: scroll;
  overflow: visible;
  margin-bottom: 1rem;
}

.tab-wrapper {
  position: relative;
}

.tab-menu {
  display: inline-flex;
  white-space: nowrap;
  align-items: center;
  gap: .25rem;
  margin-left: 1rem;
  margin-right: 1rem;
  border: 1px solid #e3e8ef;
  background-color: #f8fafc;
  padding: rem-calc(4);
  border-radius: rem-calc(8);
  margin-bottom: rem-calc(8);
  height: rem-calc(33);

  .tab-item {
    font-size: rem-calc(14);
    font-weight: 600;
    line-height: rem-calc(20);
    color: #697586;
    padding: rem-calc(8) rem-calc(12);
    border-radius: rem-calc(6);
    cursor: pointer;

    &.active {
      background-color: white;
      color: #364152;
      box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.06);
      box-shadow: 0px 1px 3px 0px rgba(16, 24, 40, 0.1);
    }

    .discussion-posts-count {
      background-color: #eff4ff;
      border: 1px solid #b2ccff;
      padding: rem-calc(2) rem-calc(8);
      border-radius: rem-calc(12);
      margin-left: rem-calc(6);
      font-size: rem-calc(12);
      font-weight: 500;
    }
  }
}

@include breakpoint(tablet) {
  .tab-menu-container {
    overflow: visible;
    margin-bottom: 2rem;
  }
}

@include breakpoint(desktop) {
  .tab-menu-container {
    overflow: visible;
    margin-bottom: 2rem;
  }

  .tab-menu {
    margin-left: 0;
    margin-right: 0;
    margin-bottom: rem-calc(8);
  }
}
</style>
